import React from 'react';
import CollapsibleTable from './CollapsibleTable';

interface EnhancedTableViewProps {
  headers: string[];
  data: any[];
  title?: string;
  pdfMode?: boolean;
  maxInitialRows?: number;
  showFullContent?: boolean;
  showExportButtons?: boolean;
  className?: string;
  images?: string[];
  onImageClick?: (imageSrc: string) => void;
  creativeIndexExtractor?: (creativeName: string) => number;
  isExporting?: boolean;
  lastRowIsSummary?: boolean;
}

/**
 * Enhanced TableView component that wraps CollapsibleTable with TableView-compatible props
 * This provides a smooth migration path from the old TableView to the new CollapsibleTable
 */
const EnhancedTableView: React.FC<EnhancedTableViewProps> = ({
  headers,
  data,
  title = '',
  pdfMode = false,
  maxInitialRows = 5,
  showFullContent = true,
  showExportButtons = true,
  className = '',
  images,
  onImageClick,
  creativeIndexExtractor,
  isExporting = false,
  lastRowIsSummary = false,
}) => {
  const getImageSrc = (imageData: string) => {
    if (imageData.startsWith('data:image/')) {
      return imageData;
    }
    return `data:image/jpeg;base64,${imageData}`;
  };
  // Convert props to CollapsibleTable format
  const tableData = {
    type: 'table' as const,
    title: title || 'Data Table',
    headers,
    data: data.map((row) => {
      // Handle both array and object formats
      if (Array.isArray(row)) {
        const obj: Record<
          string,
          string | { type: 'image'; src: string; alt: string; onImageClick: () => void }
        > = {};
        headers.forEach((header, index) => {
          const cellValue = row[index];

          // Check if this is the second column (index 1) and we have images - place image after Rank
          if (index === 1 && images && onImageClick) {
            let creativeIndex = -1;

            if (creativeIndexExtractor) {
              // Use custom extractor function for new format
              const creativeColumnValue = row[2]; // Third column should be "Creative" in new format
              if (typeof creativeColumnValue === 'string') {
                creativeIndex = creativeIndexExtractor(creativeColumnValue);
              }
            } else {
              // Legacy behavior: look at the last column to extract creative index
              const lastColumnValue = row[headers.length - 1];
              if (typeof lastColumnValue === 'string') {
                // Extract creative index from "Creative X" format
                const creativeMatch = lastColumnValue.match(/Creative (\d+)/);
                if (creativeMatch) {
                  creativeIndex = parseInt(creativeMatch[1]) - 1;
                }
              }
            }

            if (creativeIndex >= 0 && images[creativeIndex]) {
              obj[header] = {
                type: 'image',
                src: images[creativeIndex],
                alt: `Creative ${creativeIndex + 1}`,
                onImageClick: () => onImageClick(getImageSrc(images[creativeIndex])),
              };
              return;
            }
          }

          obj[header] = cellValue?.toString() || '';
        });
        return obj;
      }
      // Convert all values to strings
      const stringRow: Record<
        string,
        string | { type: 'image'; src: string; alt: string; onImageClick: () => void }
      > = {};
      headers.forEach((header) => {
        stringRow[header] = row[header]?.toString() || '';
      });
      return stringRow;
    }),
  };

  return (
    <CollapsibleTable
      table={tableData}
      questionType="table"
      maxInitialRows={maxInitialRows}
      showFullContent={showFullContent}
      showExportButtons={showExportButtons}
      pdfMode={pdfMode}
      className={className}
      images={images}
      isExporting={isExporting}
      lastRowIsSummary={lastRowIsSummary}
    />
  );
};

export default EnhancedTableView;
